import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { organizationAPI, instanceAPI } from '../services/api';
import Pagination from '../components/Pagination';
import OrganizationDialog from '../components/dialogs/OrganizationDialog';
import './Organizations.css';

interface Instance {
  _id: string;
  name: string;
  is_default?: boolean;
}

interface Organization {
  _id: string;
  key: string;
  name: string;
  instance_id?: string;
  active: boolean;
  created_at: string;
}

interface OrganizationsProps {
  onNavigateToPeople?: (organizationId: string) => void;
}

function Organizations({ onNavigateToPeople }: OrganizationsProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ key: '', name: '', instance_id: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDialog, setShowDialog] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    loadOrganizations();
    loadInstances();
  }, [sortBy, sortOrder]);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const response = await organizationAPI.list(sortBy, sortOrder);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
      showNotification('Erro ao carregar organizações', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadInstances = async () => {
    try {
      const response = await instanceAPI.list();
      setInstances(response.data);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.key.toLowerCase().includes(searchName.toLowerCase()) ||
    org.name.toLowerCase().includes(searchName.toLowerCase()) ||
    org._id.toLowerCase().includes(searchName.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName]);

  const getInstanceName = (instanceId?: string) => {
    if (!instanceId) return '-';
    const instance = instances.find(i => i._id === instanceId);
    return instance ? instance.name : '-';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const dataToSend = {
        ...formData,
        instance_id: formData.instance_id || null
      };
      
      if (editingId) {
        await organizationAPI.update(editingId, dataToSend);
        showNotification('Organização atualizada com sucesso!', 'success');
      } else {
        await organizationAPI.create({ ...dataToSend, active: true });
        showNotification('Organização criada com sucesso!', 'success');
      }
      
      closeDialog();
      loadOrganizations();
    } catch (error: any) {
      console.error('Erro ao salvar organização:', error);
      const msg = error?.response?.data?.detail || 'Erro ao salvar organização';
      showNotification(msg, 'error');
    }
  };

  const handleEdit = (org: Organization) => {
    setFormData({ 
      key: org.key,
      name: org.name,
      instance_id: org.instance_id || ''
    });
    setEditingId(org._id);
    setShowDialog(true);
  };

  const handleNewOrganization = () => {
    const defaultInstance = instances.find((i) => i.is_default);
    setFormData({ key: '', name: '', instance_id: defaultInstance?._id || '' });
    setEditingId(null);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setFormData({ key: '', name: '', instance_id: '' });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar esta organização?')) {
      try {
        await organizationAPI.delete(id);
        showNotification('Organização deletada com sucesso!', 'success');
        loadOrganizations();
      } catch (error) {
        console.error('Erro ao deletar:', error);
        showNotification('Erro ao deletar organização', 'error');
      }
    }
  };

  return (
    <div className="organizations-container">
      <div className="page-header">
        <h1>Organizações</h1>

        <button 
          className="btn-add"
          onClick={handleNewOrganization}
          title="Adicionar nova organização"
        >
          <span className="btn-icon">+</span>
          <span>Adicionar</span>
        </button>
      </div>

      {/* Dialog para criar/editar organização */}
      <OrganizationDialog
        show={showDialog}
        editingId={editingId}
        formData={formData}
        instances={instances}
        onSubmit={handleSubmit}
        onChange={(data) => setFormData({ ...formData, ...data })}
        onClose={closeDialog}
      />

      {/* Barra de busca e ordenação */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="🔍 Buscar por key, nome ou ID..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="search-input"
          />
          {searchName && (
            <button 
              className="clear-search"
              onClick={() => setSearchName('')}
              title="Limpar busca"
            >
              ✕
            </button>
          )}
        </div>
        
        <select 
          className="sort-select"
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('-') as ['name' | 'created_at', 'asc' | 'desc'];
            setSortBy(field);
            setSortOrder(order);
          }}
        >
          <option value="name-asc">Nome (A-Z)</option>
          <option value="name-desc">Nome (Z-A)</option>
          <option value="created_at-desc">Mais recentes</option>
          <option value="created_at-asc">Mais antigas</option>
        </select>
        
        <span className="search-count">
          {filteredOrganizations.length} organizaç{filteredOrganizations.length !== 1 ? 'ões' : 'ão'}
        </span>
      </div>

      {/* Tabela de organizações */}
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          <div className="table-container">
            <table className="organizations-table org-list-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Key</th>
                  <th>Instância</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrganizations.length > 0 ? (
                  paginatedOrganizations.map((org) => (
                    <tr key={org._id}>
                      <td>
                        <span className="name-icon">🏢</span>{org.name}
                        <br />
                        <span className="org-id-label">{org._id}</span>
                      </td>
                      <td>{org.key}</td>
                      <td>{getInstanceName(org.instance_id)}</td>
                      <td className="actions-cell">
                        <Link 
                          to={`/people/${org._id}`}
                          className="btn-action btn-people"
                          title="Ver pessoas desta organização"
                        >
                          <span className="btn-icon">👥</span>
                        </Link>
                        <Link
                          to={`/search/${org._id}`}
                          className="btn-action btn-search"
                          title="Ir para busca facial desta organização"
                        >
                          <span className="btn-icon">🔍</span>
                        </Link>
                        <button 
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(org)}
                          title="Editar organização"
                        >
                          <span className="btn-icon">✏️</span>
                        </button>
                        <button 
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(org._id)}
                          title="Deletar organização"
                        >
                          <span className="btn-icon">🗑️</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-results">
                      Nenhuma organização encontrada{searchName && ` com o nome "${searchName}"`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Notificação */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.type === 'success' ? '✅' : '❌'}</span>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default Organizations;
